import Routers from 'constants/routers';
import React, { Fragment, Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import TCommonLayout from 'components/Layout';
import Loading from 'components/Loading';
import RoleGuard from 'components/RoleGuard';
import { useAuth } from 'context/AuthContext';
export interface IRoutesState {
  exact?: boolean;
  path?: string;
  guard?: React.FC<any>;
  layout?: React.FC<any>;
  component?: any;
  routes?: IRoutesState[];
}

export const renderRoutes = (routes: IRoutesState[]) => (
  <Suspense fallback={<Loading />}>
    <Routes>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;
        return (
          <Route
            key={i}
            path={route.path}
            element={
              <Guard>
                <Layout>{route.routes ? renderRoutes(route.routes) : <Component />}</Layout>
              </Guard>
            }
          />
        );
      })}
    </Routes>
  </Suspense>
);

const withRoleGuard = (roles: string[]) => ({ children }: any) =>
  <RoleGuard allowedRoles={roles}>{children}</RoleGuard>;

const RoleRedirect = () => {
  const user = useAuth();

  if (!user.user?.role) return <Navigate to="/login" replace />;

  switch (user.user?.role) {
    case 'training_coordinator':
      return <Navigate to={Routers.Training} replace />;
    case 'admin':
    case 'human_resource':
    case 'leader':
    case 'board_of_directors':
    case 'chief_operating_officer':
      return <Navigate to={Routers.Candidates} replace />;
    default:
      return <Navigate to={Routers.NotFound} replace />;
  }
};

const routes: IRoutesState[] = [
  {
    path: Routers.CandidateEssay,
    component: lazy(() => import('pages/Candidates/test'))
  },
  {
    path: Routers.Login,
    component: lazy(() => import('pages/Login'))
  },
  {
    path: Routers.AutoRegister,
    component: lazy(() => import('pages/AutoRegister'))
  },
  {
    path: Routers.All,
    layout: TCommonLayout,
    routes: [
      // Job
      {
        path: Routers.Dashboard,
        component: RoleRedirect
      },
      {
        path: Routers.Jobs,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Jobs'))
      },

      {
        path: Routers['CreateJob'],
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Jobs/create'))
      },
      {
        path: `${Routers['DetailJob']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Jobs/detail'))
      },
      // Articles
      {
        path: Routers.Articles,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Articles'))
      },
      {
        path: Routers['CreateArticle'],
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Articles/create'))
      },
      {
        path: `${Routers['DetailArticle']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Articles/detail'))
      },
      {
        path: `${Routers['InfoArticle']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Articles/info'))
      },
      // Candidate
      {
        path: Routers.Candidates,
        guard: withRoleGuard(['admin', 'leader', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Candidates'))
      },
      {
        path: `${Routers.Information}/:id`,
        guard: withRoleGuard(['admin', 'leader', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Candidates/information/[id]'))
      },
      {
        path: `${Routers['CreateMeeting']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Candidates/create/[id]'))
      },
      {
        path: `${Routers['CreateMeeting2']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/Candidates/create2/[id]'))
      },
      {
        path: `${Routers['CandidateHistory']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Candidates/history/[id]'))
      },
      // User Management
      {
        path: Routers.Users,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/Users'))
      },
      // Training Mangement
      {
        path: Routers.Training,
        guard: withRoleGuard(['admin', 'human_resource', 'training_coordinator']),
        component: lazy(() => import('pages/Training'))
      },
      {
        path: `${Routers['TrainingView']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'training_coordinator']),
        component: lazy(() => import('pages/Training/view'))
      },
      {
        path: `${Routers['TrainingEdit']}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'training_coordinator']),
        component: lazy(() => import('pages/Training/edit/[id]'))
      },
      // Hashtags
      {
        path: Routers.Hashtags,
        guard: withRoleGuard(['admin', 'human_resource', 'training_coordinator']),
        component: lazy(() => import('pages/Hashtags'))
      },
      {
        path: Routers.Marathon,
        guard: withRoleGuard(['admin', 'human_resource', 'training_coordinator']),
        component: lazy(() => import('pages/MarathonEvent'))
      },
      // Test Management
      {
        path: Routers.TestManagement,
        guard: withRoleGuard(['admin', 'leader', 'board_of_directors', 'chief_operating_officer', 'human_resource']),
        component: lazy(() => import('pages/Test'))
      },
      {
        path: Routers.Information,
        guard: withRoleGuard(['admin', 'leader',]),
        component: lazy(() => import('pages/Candidates/information/[id]'))

      },
      {
        path: Routers.CreateTest,
        guard: withRoleGuard(['admin', 'leader', 'board_of_directors', 'chief_operating_officer', 'human_resource']),
        component: lazy(() => import('pages/Test/create'))
      },
      {
        path: `${Routers.EditTest}/:id`,
        guard: withRoleGuard(['admin', 'leader', 'board_of_directors', 'chief_operating_officer', 'human_resource']),
        component: lazy(() => import('pages/Test/edit'))
      },
      {
        path: `${Routers.DetailTest}/:id`,
        guard: withRoleGuard(['admin', 'leader', 'board_of_directors', 'chief_operating_officer', 'human_resource']),
        component: lazy(() => import('pages/Test/detail'))
      },
      {
        path: `${Routers.DetailCadidateTest}/:id`,
        guard: withRoleGuard(['admin', 'leader', 'board_of_directors', 'chief_operating_officer', 'human_resource']),
        component: lazy(() => import('pages/Test/detailCandidateTest'))
      },
      // BOD Management
      {
        path: Routers.BODInterviewManagement,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/BODInterview/InterviewList'))
      },
      {
        path: `${Routers.DetailEvaluate}/:id`,
        guard: withRoleGuard(['admin', 'human_resource', 'board_of_directors', 'chief_operating_officer']),
        component: lazy(() => import('pages/BODInterview/detail/[id]'))
      },
      // Component Test Page
      {
        path: Routers.ComponentTest,
        component: lazy(() => import('pages/ComponentTest'))
      },
      // Role Management
      {
        path: Routers.RoleManagement,
        guard: withRoleGuard(['admin', 'human_resource']),
        component: lazy(() => import('pages/RoleManagement'))
      },
      // Content Moderation
      {
        path: Routers.Moderation,
        guard: withRoleGuard(['admin', 'human_resource', 'leader']),
        component: lazy(() => import('pages/Moderation'))
      },

      {
        path: '*',
        component: () => <Navigate to={Routers.NotFound} replace />
      }
    ]
  }
];

export default routes;
